// Native health-data wrapper (Apple HealthKit on iOS, Health Connect on Android)
// via `capacitor-health`. Read-only: we only ever READ workouts, to pair the
// morning-after session with last night's bender. Everything stays on-device.
//
// All functions are safe to call on web: they no-op / return empty.
import { Capacitor } from '@capacitor/core';

// MINIMUM SCOPE (Play Health Connect policy — versionCode 3 was rejected for
// requesting calories + heart rate, which the Rap Sheet only ever displayed as
// garnish). The feature needs the workout itself and its distance. Nothing else.
const PERMS = ['READ_WORKOUTS', 'READ_DISTANCE'];

let _healthPromise = null;
// Wrapped in a plain object for the same reason as notify.js: never resolve a
// promise WITH the Capacitor proxy itself (the bridge rejects proxy.then()).
function loadHealth() {
  if (typeof window === 'undefined' || !Capacitor.isNativePlatform?.()) return null;
  if (!_healthPromise) {
    _healthPromise = import('capacitor-health').then((m) => ({ Health: m.Health }));
  }
  return _healthPromise;
}

export function isHealthSupported() {
  return typeof window !== 'undefined' && !!Capacitor.isNativePlatform?.();
}

/** Store-correct name for the health source, so the UI can name it explicitly. */
export function healthSourceLabel() {
  return Capacitor.getPlatform?.() === 'android' ? 'Health Connect' : 'Apple Health';
}

/** Ask for read permission. iOS can't report grant state, so a non-throwing return is treated as OK. */
export async function ensureHealthPermission() {
  const wrap = await loadHealth();
  if (!wrap) return false;
  try {
    const { available } = await wrap.Health.isHealthAvailable();
    if (!available) return false;
    await wrap.Health.requestHealthPermissions({ permissions: PERMS });
    return true;
  } catch (e) {
    console.warn('Health permission failed:', e);
    return false;
  }
}

// Map the many platform-specific workout type strings onto our small set.
export function normalizeWorkoutType(raw) {
  const t = String(raw || '').toLowerCase().replace(/[^a-z]/g, '');
  if (t.includes('run')) return 'run';
  if (t.includes('hik')) return 'hike';
  if (t.includes('walk')) return 'walk';
  if (t.includes('cycl') || t.includes('bik')) return 'ride';
  if (t.includes('swim')) return 'swim';
  if (t.includes('strength') || t.includes('weight')) return 'strength';
  if (t.includes('interval') || t.includes('hiit') || t.includes('crossfit') || t.includes('crosstrain')) return 'hiit';
  if (t.includes('yoga') || t.includes('pilates') || t.includes('stretch')) return 'yoga';
  return 'default';
}

/** Query workouts in [startMs, endMs] and return them in our normalized shape. */
export async function queryWorkouts({ startMs, endMs }) {
  const wrap = await loadHealth();
  if (!wrap) return [];
  try {
    const res = await wrap.Health.queryWorkouts({
      startDate: new Date(startMs).toISOString(),
      endDate: new Date(endMs).toISOString(),
      includeHeartRate: false, // not requested — minimum scope
      includeRoute: false,
      includeSteps: false,
    });
    return (res?.workouts || []).map((w) => {
      const durationMin = (Number(w.duration) || 0) / 60;
      const distanceKm = w.distance != null ? Number(w.distance) / 1000 : 0;
      return {
        id: w.id || `${w.startDate}-${w.workoutType}`,
        type: normalizeWorkoutType(w.workoutType),
        rawType: w.workoutType,
        startMs: Date.parse(w.startDate),
        endMs: Date.parse(w.endDate),
        durationMin,
        distanceKm,
        paceSecPerKm: distanceKm > 0.2 ? (durationMin * 60) / distanceKm : null,
        calories: null, // not read (minimum scope)
        avgHr: null,    // not read (minimum scope)
        hasRoute: false,
        source: w.sourceName || null,
      };
    });
  } catch (e) {
    console.warn('queryWorkouts failed:', e);
    return [];
  }
}
